import { useState } from 'react';
import { useAuthStore } from '../../hooks/useAuth';
import { ShieldOff, Trash2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDateInTimeZone, formatTimeInTimeZone } from '../../utils/timezoneDisplay';

// ─────────────────────────────────────────────────────
//  BlackoutList — upcoming/active dock blackout windows
//  V1.3B: Collapsible list on DockManagement
// ─────────────────────────────────────────────────────

export interface BlackoutWindow {
    id: string;
    warehouseId: string;
    dockId: string | null;
    dockNumber: string | null;
    startAt: string;
    endAt: string;
    reason: string;
    createdBy: string | null;
    createdByName: string | null;
    createdAt: string;
    isWarehouseWide: boolean;
}

interface BlackoutListProps {
    blackouts: BlackoutWindow[];
    loading: boolean;
    onDeleted: () => void;
}

export function BlackoutList({ blackouts, loading, onDeleted }: BlackoutListProps) {
    const { token, user } = useAuthStore();
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    const fmtDT = (iso: string) => {
        return `${formatDateInTimeZone(iso, user?.timeZone, false)} ${formatTimeInTimeZone(iso, user?.timeZone)}`;
    };

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            const res = await fetch(`/api/docks/blackouts/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to delete blackout');
            }
            toast.success('Blackout removed');
            setConfirmDeleteId(null);
            onDeleted();
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setDeletingId(null);
        }
    };



    const now = Date.now();
    const activeBlackouts = blackouts.filter(b => new Date(b.endAt).getTime() > now);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8 text-slate-400 dark:text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading blackouts...
            </div>
        );
    }

    if (activeBlackouts.length === 0) {
        return (
            <div className="text-center py-6 text-slate-400 dark:text-slate-500 text-sm">
                No upcoming blackout windows.
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {activeBlackouts.map(b => {
                const isActive = new Date(b.startAt).getTime() <= now && new Date(b.endAt).getTime() > now;
                const isFuture = new Date(b.startAt).getTime() > now;

                return (
                    <div
                        key={b.id}
                        className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border transition-colors ${
                            isActive
                                ? 'bg-red-50/60 dark:bg-red-950/20 border-red-200 dark:border-red-800/50'
                                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                        }`}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <ShieldOff className={`w-4 h-4 shrink-0 ${isActive ? 'text-red-500' : 'text-slate-400 dark:text-slate-500'}`} />
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {/* Dock badge */}
                                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                        b.isWarehouseWide
                                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                                    }`}>
                                        {b.isWarehouseWide ? 'All Docks' : `Dock ${b.dockNumber}`}
                                    </span>
                                    {/* Status badge */}
                                    {isActive && (
                                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500 text-white animate-pulse">
                                            Active Now
                                        </span>
                                    )}
                                    {isFuture && (
                                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                                            Upcoming
                                        </span>
                                    )}
                                    {/* Reason */}
                                    <span className="text-xs text-slate-600 dark:text-slate-400">{b.reason}</span>
                                </div>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 tabular-nums mt-0.5">
                                    {fmtDT(b.startAt)} — {fmtDT(b.endAt)}
                                    {b.createdByName && <span className="ml-2 text-slate-400">by {b.createdByName}</span>}
                                </p>
                            </div>
                        </div>

                        {/* Delete action */}
                        <div className="shrink-0">
                            {confirmDeleteId === b.id ? (
                                <div className="flex items-center gap-1.5">
                                    <button
                                        onClick={() => setConfirmDeleteId(null)}
                                        className="text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 px-1.5 py-1"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => handleDelete(b.id)}
                                        disabled={deletingId === b.id}
                                        className="text-[10px] font-bold text-red-600 hover:text-red-500 px-1.5 py-1 flex items-center gap-1"
                                    >
                                        {deletingId === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setConfirmDeleteId(b.id)}
                                    className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                                    title="Remove blackout"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
